import { HttpContext, HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, EMPTY, switchMap, throwError } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { SKIP_GLOBAL_LOADER } from '../http/http-context-tokens';
import { isWinerySuspended } from '../../shared/utils/winery-suspended.util';

export const jwtInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const token = authService.getAccessToken();

  if (req.url.includes('/token')) {
    return next(req);
  }

  if (token) {
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // Not an expiry: the refresh tokens are gone for good, so the sign-in page says the account is paused.
  const endSessionAsPaused = () => {
    authService.clearSession();
    void router.navigate(['/login'], { queryParams: { paused: 1 } });
    return EMPTY;
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (isWinerySuspended(error)) {
        return endSessionAsPaused();
      }

      if (error.status === 401 && authService.getRefreshToken()) {
        return authService.refreshTokenOnce().pipe(
          switchMap(() => {
            const newToken = authService.getAccessToken();
            req = req.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken}`,
              },
              context: req.context.set(SKIP_GLOBAL_LOADER, true),
            });
            return next(req);
          }),
          catchError((refreshError) => {
            if (isWinerySuspended(refreshError)) {
              return endSessionAsPaused();
            }
            // The refresh token survives offline and 5xx failures; only a rejected one ends the session.
            if (authService.getRefreshToken()) {
              return throwError(() => refreshError);
            }
            void router.navigate(['/login'], { queryParams: { returnUrl: router.url, expired: 1 } });
            return EMPTY;
          }),
        );
      }

      if (error.status === 401 && token) {
        // Signed in per memory but nothing left to refresh with: same expiry path as a rejected refresh.
        authService.clearSession();
        void router.navigate(['/login'], { queryParams: { returnUrl: router.url, expired: 1 } });
        return EMPTY;
      }

      return throwError(() => error);
    }),
  );
};
